import { create } from "zustand";
import { CompanySettings, SettingSection, ValidationRequest, ValidationResponse } from "./data";
import { 
    retrieveCompanySettings, 
    updateCompanySettings,
    updateSettingSection,
    validateSettings,
    retrieveAuditTrail
} from "./actions";

type CompanySettingsStoreProps = {
    loading: boolean
    settings: CompanySettings | null
    auditTrail: any | null
    error: string
    
    // Actions
    retrieveSettings: () => void
    updateSettings: (settings: CompanySettings) => Promise<void>
    updateSection: (section: string, data: SettingSection) => Promise<void>
    validateSection: (request: ValidationRequest) => Promise<ValidationResponse>
    retrieveAudit: () => void
    clearError: () => void
}

export const useCompanySettingsStore = create<CompanySettingsStoreProps>((set, getState) => ({
    loading: false,
    settings: null,
    auditTrail: null,
    error: "",
    
    retrieveSettings() {
        set(state => ({ loading: true, error: "" }))
        retrieveCompanySettings()
            .then(data => set(state => ({ settings: data, loading: false })))
            .catch(err => set(state => ({ loading: false, error: err.message || "Failed to retrieve settings" })))
    },
    
    async updateSettings(settings) {
        try {
            set(state => ({ loading: true, error: "" }))
            await updateCompanySettings(settings)
            set(state => ({ settings: settings, loading: false }))
        } catch (error: any) {
            set(state => ({ loading: false, error: error.message || "Failed to update settings" }))
            throw error
        }
    },
    
    async updateSection(section, data) {
        try {
            set(state => ({ loading: true, error: "" }))
            await updateSettingSection(section, data)
            // Update the local state
            const currentSettings = getState().settings
            if (currentSettings) {
                const updatedSettings = {
                    ...currentSettings,
                    settings: {
                        ...currentSettings.settings,
                        [section]: data
                    }
                }
                set(state => ({ settings: updatedSettings, loading: false }))
            }
        } catch (error: any) {
            set(state => ({ loading: false, error: error.message || "Failed to update section" }))
            throw error
        }
    },
    
    async validateSection(request) {
        try {
            const response = await validateSettings(request)
            return response
        } catch (error: any) {
            throw error
        }
    },
    
    retrieveAudit() {
        retrieveAuditTrail()
            .then(data => set(state => ({ auditTrail: data })))
            .catch(err => set(state => ({ error: err.message || "Failed to retrieve audit trail" })))
    },
    
    clearError() {
        set(state => ({ error: "" }))
    }
}))